import styled from '@emotion/styled'
import Autocomplete from '@mui/material/Autocomplete'
import TextField from '@mui/material/TextField'
import axios from 'axios'
import { useEffect, useState } from 'react'
import { Button } from 'react-bootstrap'
import { useNavigate } from 'react-router-dom'
import Breakpoint from '../../Breakpoint'
import Label from '../../components/form/Label'
import catchError from '../../components/global/CatchError'
import Loader from '../../components/global/Loader'
import SearchInput from '../../components/global/SearchInput'
import Title from '../../components/global/Title'
import { SERVER_URL, apiHeader } from '../../config'
import { useLangSelected } from '../../hooks/useLangSelected'
import { Skill } from '../../types/skill.type'
import { renderHtmlContent } from '../../utils/htmlRenderer'
import CoachExerciseFormModal from './CoachExerciseFormModal'

const StyledButton = styled(Button)`
    font-size: 1.6rem;

    &:active {
        transform: scale(0.95);
    }
`

const Btn = styled(StyledButton)`
    margin-right: 8px;
    margin-bottom: 8px;

    @media (max-width: ${Breakpoint.TABLET_XS}) {
        margin-right: 0;
        width: 100%;
    }
`

const TitleWrapper = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 24px;
    width: 100%;

    @media (max-width: 750px) {
        flex-direction: column;
        align-items: flex-start;
        gap: 12px;
    }
`

const FilterContainer = styled.div`
    display: flex;
    gap: 32px;
    margin-bottom: 28px;
    flex-wrap: wrap;
    align-items: flex-end;
    width: 100%;
    background: white;
    padding: 20px;
    border-radius: 8px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);

    @media (max-width: 768px) {
        flex-direction: column;
        align-items: stretch;
        gap: 16px;
    }
`

const FilterGroup = styled.div`
    display: flex;
    flex-direction: column;
    min-width: 200px;
`

const FilterSelect = styled.select`
    padding: 8px 12px;
    border-radius: 4px;
    border: 1px solid #d3d3d3;
    font-size: 1.6rem;
    height: 38px;
    width: 100%;
    background-color: white;

    &:focus {
        border-color: #80bdff;
        outline: 0;
        box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
    }
`

export type Intensity = 'LOW' | 'MEDIUM' | 'HIGH'

interface Exercise {
    id: number
    title: string
    description: string
    peopleCount: number
    intensity: Intensity
    skills: Skill[]
}

const intensityLabels: Record<Intensity, string> = {
    LOW: 'Basse',
    MEDIUM: 'Moyenne',
    HIGH: 'Haute',
}

export default function CoachExercises() {
    const renderExerciseList = () => {
        if (loading) {
            return <Loader />
        }

        if (exercises.length === 0) {
            return <NoResult>Aucun exercice trouvé.</NoResult>
        }

        return exercises.map((ex) => (
            <ExerciseCard key={ex.id}>
                <CardTitle>{ex.title}</CardTitle>
                <CardMeta>
                    <MetaItem>
                        <b>👥</b> {ex.peopleCount} personne{ex.peopleCount > 1 ? 's' : ''}
                    </MetaItem>
                    <MetaItem>
                        <b>⚡</b> Intensité :{' '}
                        <IntensityBadge intensity={ex.intensity}>{intensityLabels[ex.intensity]}</IntensityBadge>
                    </MetaItem>
                    {(ex.skills?.length ?? 0) > 0 && (
                        <MetaItem>
                            <b>🎯</b> Compétences :
                            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginTop: '4px' }}>
                                {ex.skills.map((skill) => (
                                    <SkillsBadge key={skill.id}>{skill.name}</SkillsBadge>
                                ))}
                            </div>
                        </MetaItem>
                    )}
                </CardMeta>
                {ex.description && <CardDesc dangerouslySetInnerHTML={renderHtmlContent(ex.description)} />}
                <CardActions>
                    <Btn variant="primary" onClick={() => handleEdit(ex)} disabled={formLoading && deleteId === ex.id}>
                        Modifier
                    </Btn>
                    <Btn
                        variant="danger"
                        onClick={() => handleDelete(ex.id)}
                        disabled={formLoading && deleteId === ex.id}
                    >
                        {formLoading && deleteId === ex.id ? 'Suppression...' : 'Supprimer'}
                    </Btn>
                </CardActions>
            </ExerciseCard>
        ))
    }
    const [skills, setSkills] = useState<Skill[]>([])
    const [exercises, setExercises] = useState<Exercise[]>([])
    const [availableIntensities, setAvailableIntensities] = useState<Intensity[]>([])
    const [availablePeopleCounts, setAvailablePeopleCounts] = useState<number[]>([])
    const [availableSkills, setAvailableSkills] = useState<Skill[]>([])
    const [loading, setLoading] = useState(false)
    const [filters, setFilters] = useState({
        peopleCount: '',
        intensity: '',
        skillIds: [] as number[],
        searchTerm: '',
    })
    const [isModalOpen, setIsModalOpen] = useState(false)
    const [editExercise, setEditExercise] = useState<Exercise | undefined>()
    const [deleteId, setDeleteId] = useState<number | null>(null)
    const [formLoading, setFormLoading] = useState(false)

    const navigate = useNavigate()
    const lng = useLangSelected()

    useEffect(() => {
        axios.get(`${SERVER_URL}/skills`, apiHeader()).then((r) => setSkills(r.data))
    }, [])

    const fetchExercises = () => {
        setLoading(true)
        const params = new URLSearchParams()
        if (filters.searchTerm) params.append('search', filters.searchTerm)
        if (filters.skillIds.length > 0) {
            filters.skillIds.forEach((id) => params.append('skillIds', id.toString()))
        }

        axios
            .get(`${SERVER_URL}/exercises?${params.toString()}`, apiHeader())
            .then((r) => {
                setExercises(r.data)
                // Extraire les valeurs uniques pour les filtres
                const uniqueIntensities = Array.from(new Set(r.data.map((e: Exercise) => e.intensity)))
                const uniquePeopleCounts = Array.from(new Set(r.data.map((e: Exercise) => e.peopleCount))).sort(
                    (a, b) => (a as number) - (b as number)
                )
                // Extraire toutes les compétences uniques
                const allSkills: Skill[] = r.data.flatMap((e: Exercise) => e.skills)
                const uniqueSkills = Array.from(new Map(allSkills.map((skill: Skill) => [skill.id, skill])).values())

                setAvailableIntensities(uniqueIntensities as Intensity[])
                setAvailablePeopleCounts(uniquePeopleCounts as number[])
                setAvailableSkills(uniqueSkills)
            })
            .catch((error) => {
                if (error.response?.status === 401) {
                    catchError({ status: 401, message: 'Votre session a expiré' }, () => {
                        localStorage.removeItem('tokenCoach')
                        navigate(`/${lng}/login`)
                    })
                } else {
                    console.error('Erreur lors du chargement des exercices:', error)
                }
            })
            .finally(() => setLoading(false))
    }

    useEffect(() => {
        fetchExercises()
    }, [filters])

    const handleCreate = () => {
        setEditExercise(undefined)
        setIsModalOpen(true)
    }

    const handleEdit = (ex: Exercise) => {
        setEditExercise(ex)
        setIsModalOpen(true)
    }

    const handleDelete = (id: number) => {
        setDeleteId(id)
        setFormLoading(true)
        axios
            .delete(`${SERVER_URL}/exercises/${id}`, apiHeader())
            .then(fetchExercises)
            .finally(() => {
                setFormLoading(false)
                setDeleteId(null)
            })
    }

    const handleFormSuccess = () => {
        setIsModalOpen(false)
        setEditExercise(undefined)
        fetchExercises()
    }

    return (
        <>
            <TitleWrapper>
                <Title>Exercices</Title>
                <StyledButton className="btn btn-primary" onClick={handleCreate}>
                    Ajouter un exercice
                </StyledButton>
            </TitleWrapper>
            <CoachExerciseFormModal
                show={isModalOpen}
                onClose={() => {
                    setIsModalOpen(false)
                    setEditExercise(undefined)
                }}
                exercise={
                    editExercise
                        ? {
                              ...editExercise,
                              skillIds: editExercise.skills ? editExercise.skills.map((s) => s.id) : [],
                          }
                        : undefined
                }
                onSuccess={handleFormSuccess}
                skills={skills}
            />
            <FilterContainer>
                <FilterGroup style={{ flex: 1, maxWidth: '300px' }}>
                    <SearchInputCustom
                        value={filters.searchTerm}
                        onChange={(e) => setFilters((f) => ({ ...f, searchTerm: e.target.value }))}
                        placeholder="Rechercher un exercice..."
                    />
                </FilterGroup>

                <FilterGroup>
                    <Label>Intensité</Label>
                    <FilterSelect
                        value={filters.intensity}
                        onChange={(e) => setFilters((f) => ({ ...f, intensity: e.target.value }))}
                        className="form-select"
                    >
                        <option value="">Toutes</option>
                        {availableIntensities.map((intensity) => (
                            <option key={intensity} value={intensity}>
                                {intensityLabels[intensity]}
                            </option>
                        ))}
                    </FilterSelect>
                </FilterGroup>

                <FilterGroup>
                    <Label>Nombre de personnes</Label>
                    <FilterSelect
                        value={filters.peopleCount}
                        onChange={(e) => setFilters((f) => ({ ...f, peopleCount: e.target.value }))}
                        className="form-select"
                    >
                        <option value="">Tous</option>
                        {availablePeopleCounts.map((count) => (
                            <option key={count} value={count}>
                                {count} personne{count > 1 ? 's' : ''}
                            </option>
                        ))}
                    </FilterSelect>
                </FilterGroup>

                <FilterGroup style={{ flex: 1, maxWidth: '400px' }}>
                    <Label>Compétences</Label>
                    <Autocomplete
                        multiple
                        options={availableSkills}
                        getOptionLabel={(option) => option.name}
                        value={availableSkills.filter((skill) => filters.skillIds.includes(skill.id))}
                        onChange={(_, value) => {
                            setFilters((f) => ({ ...f, skillIds: value.map((v) => v.id) }))
                        }}
                        disableCloseOnSelect
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                variant="outlined"
                                size="small"
                                placeholder="Toutes"
                                style={{ fontSize: '1.6rem' }}
                            />
                        )}
                        sx={{
                            background: 'white',
                            '& .MuiInputBase-root': {
                                fontSize: '1.6rem',
                                minHeight: '38px',
                                padding: '4px 8px',
                            },
                        }}
                    />
                </FilterGroup>
            </FilterContainer>
            <ExerciseList>{renderExerciseList()}</ExerciseList>
        </>
    )
}

const SearchInputCustom = styled(SearchInput)`
    margin: 0;
`

const ExerciseList = styled.ul`
    list-style: none;
    padding: 0;
    margin: 0;
`

const CardActions = styled.div`
    display: flex;
    gap: 10px;
    margin-top: 16px;
    justify-content: flex-end;
`

const ExerciseCard = styled.li`
    border: 1px solid #e0e0e0;
    border-radius: 10px;
    margin-bottom: 16px;
    padding: 20px 24px;
    background: white;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
    transition: transform 0.2s, box-shadow 0.2s;

    &:hover {
        box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
    }
`

const CardTitle = styled.h3`
    margin: 0 0 12px 0;
    font-size: 2rem;
    color: var(--primary-color);
    font-weight: 600;
`

const CardMeta = styled.div`
    display: flex;
    flex-direction: column;
    flex-wrap: wrap;
    margin-bottom: 8px;
    justify-content: center;
`

const MetaItem = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 6px;
`

interface IntensityBadgeProps {
    intensity: 'LOW' | 'MEDIUM' | 'HIGH'
}

interface IntensityStyles {
    bg: string
    color: string
}

const getIntensityStyles = (intensity: 'LOW' | 'MEDIUM' | 'HIGH'): IntensityStyles => {
    switch (intensity) {
        case 'LOW':
            return { bg: '#e6f7e6', color: '#2e7d32' }
        case 'MEDIUM':
            return { bg: '#fff3e0', color: '#e65100' }
        case 'HIGH':
            return { bg: '#ffebee', color: '#c62828' }
    }
}

const IntensityBadge = styled.span<IntensityBadgeProps>(({ intensity }) => {
    const styles = getIntensityStyles(intensity)
    return {
        display: 'inline-block',
        padding: '2px 8px',
        borderRadius: '12px',
        fontWeight: 500,
        backgroundColor: styles.bg,
        color: styles.color,
    }
})

const CardDesc = styled.div`
    margin: 16px 0 0 0;
    color: #555;
    line-height: 1.5;

    p {
        margin: 0 0 1em 0;
        &:last-child {
            margin-bottom: 0;
        }
    }
`

const SkillsBadge = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    background: #f5f5f5;
    padding: 2px 10px;
    border-radius: 12px;
    color: #616161;
`

const NoResult = styled.div`
    color: #888;
    padding: 32px 0;
    text-align: center;
`
